import { Component, Input, ViewChild, ElementRef, AfterViewChecked, OnInit, OnDestroy, Optional } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AiAssistantService, ChatMessage } from './ai-assistant.service';
import { AppService } from '../../_services/app.service';

@Component({
  selector: 'app-ai-assistant',
  templateUrl: './ai-assistant.component.html',
  styleUrls: ['./ai-assistant.component.scss'],
  standalone: false
})
export class AiAssistantComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() selectedCEA: string = '';
  @Input() themeClass: string = 'light';

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  @ViewChild('inputField') private inputField!: ElementRef;

  public userInput: string = '';
  public suggestions: string[] = [
    '¿Por qué se retienen trabajos en pendientes y cómo subsanarlos?',
    '¿Cómo funciona la regla de los 160 días en partos y abortos?',
    '¿Qué tipos de cubrición y montas reconoce SIRA?',
    '¿Cómo se clasifican los libros genealógicos y cartas?'
  ];

  private shouldScrollBottom = false;
  private destroyed = new Subject<void>();

  constructor(
    public aiSvc: AiAssistantService,
    @Optional() private appService?: AppService
  ) {}

  ngOnInit(): void {
    const isDark = this.appService ? this.appService.isDark : (localStorage.getItem('themeClass') === 'twa-dark-theme');
    if (isDark) {
      this.themeClass = 'twa-dark-theme';
    }

    if (this.appService) {
      this.appService.themeChanges
        .pipe(takeUntil(this.destroyed))
        .subscribe((dark: boolean) => {
          this.themeClass = dark ? 'twa-dark-theme' : 'light';
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollBottom) {
      this.scrollToBottom();
      this.shouldScrollBottom = false;
    }
  }

  public togglePanel(): void {
    this.aiSvc.toggleOpen();
    if (this.aiSvc.isOpen) {
      this.shouldScrollBottom = true;
      setTimeout(() => {
        if (this.inputField) {
          this.inputField.nativeElement.focus();
        }
      }, 150);
    }
  }

  public onSend(): void {
    if (!this.userInput.trim() || this.aiSvc.isGenerating) {
      return;
    }

    const text = this.userInput;
    this.userInput = '';
    this.shouldScrollBottom = true;

    this.aiSvc.sendMessage(text, { selectedCEA: this.selectedCEA });
  }

  public useSuggestion(suggestion: string): void {
    this.userInput = suggestion;
    this.onSend();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
    }
  }

  public resetChat(): void {
    this.aiSvc.resetSession();
    this.shouldScrollBottom = true;
  }

  public formatContent(content: string): string {
    if (!content) return '';

    // Ocultar bloques internos de acción técnica para el usuario
    let clean = content
      .replace(/```(?:sira-action|json)?\s*\{\s*"action"[\s\S]*?\}\s*```/gi, '')
      .replace(/<sira-action>[\s\S]*?<\/sira-action>/gi, '')
      .trim();

    // Extraer y procesar tablas Markdown (| Col | Col |)
    const tableRegex = /((?:^[ \t]*\|[^\n]+\|[ \t]*(?:\r?\n|$))(?:^[ \t]*\|[-: |]+\|[ \t]*(?:\r?\n|$))(?:^[ \t]*\|[^\n]+\|[ \t]*(?:\r?\n|$))+)/gm;
    const tables: string[] = [];
    clean = clean.replace(tableRegex, (match) => {
      const lines = match.trim().split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
      if (lines.length < 2) return match;

      const splitCells = (line: string) => line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
      const headers = splitCells(lines[0]);
      const thead = `<thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
      const rows = lines.slice(2).map(line => {
        const cells = splitCells(line);
        return `<tr>${cells.map(c => `<td>${c}</td>`).join('')}</tr>`;
      }).join('');

      const htmlTable = `<div class="ai-table-wrapper"><table class="ai-table">${thead}<tbody>${rows}</tbody></table></div>`;
      const placeholder = `__AI_TABLE_PLACEHOLDER_${tables.length}__`;
      tables.push(htmlTable);
      return '\n\n' + placeholder + '\n\n';
    });

    // Transformación básica y segura de markdown a HTML para visualización limpia
    let html = clean
      // Bloques de código ```
      .replace(/```([a-z]*)\n([\s\S]*?)```/g, '<pre class="code-block"><code>$2</code></pre>')
      // Código en línea `...`
      .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
      // Negrita **...**
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      // Cursiva *...*
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      // Listas con viñetas
      .replace(/^\s*[-*]\s+(.+)$/gm, '<li class="bullet-item">$1</li>')
      // Saltos de línea
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>');

    // Restaurar tablas aplicando formateo inline a las celdas
    tables.forEach((t, i) => {
      const formatted = t
        .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>');
      html = html.replace(`__AI_TABLE_PLACEHOLDER_${i}__`, formatted);
    });

    return html;
  }

  private scrollToBottom(): void {
    try {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
      }
    } catch (_) {}
  }
}
